import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Box,
  Collapse,
  Container,
  IconButton,
  Typography,
} from "@mui/material";
import React from "react";
import FinalCTA from "../../components/layout/FinalCTA";
import RoiHeroSection from "./RoiHeroSection";

export default function RoiCalculatorPage() {
  return (
    <Box sx={{ backgroundColor: "#ffff" }}>
      <RoiHeroSection />
      <WhyROI />
      <FAQ />
      <Disclaimer />
      <FinalCTA />
    </Box>
  );
}

function WhyROI() {
  const points = [
    {
      no: "01",
      text: "Compare multiple luxury investment opportunities side by side to identify the strongest-performing assets with confidence.",
    },
    {
      no: "02",
      text: "Understand how financing, interest rates, and leverage directly impact your real-world returns and long-term profitability.",
    },
    {
      no: "03",
      text: "Uncover hidden costs such as service charges, maintenance, and vacancies that can significantly affect net returns.",
    },
    {
      no: "04",
      text: "Make informed property investment decisions across Dubai and Sharjah with clarity, precision, and market insight.",
    },
  ];

  return (
    <Box
      sx={{
        backgroundColor: "var(--color-bg)",
        color: "var(--color-text-primary)",
        py: { xs: 8, md: 14 },
        position: "relative",
      }}
    >
      <Container maxWidth="lg">
        {/* MAIN FLEX */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 6, md: 12 },
          }}
        >
          {/* LEFT SIDE */}
          <Box sx={{ flex: "0 0 38%" }}>
            <Typography
              sx={{
                fontSize: { xs: 30, sm: 40, md: 56 },
                fontWeight: 500,
                lineHeight: 1.15,
                mb: 4,
                color: "var(--color-text-primary)",
              }}
            >
              Why use this{" "}
              <Box
                component="span"
                sx={{ color: "var(--color-gold)", fontWeight: 500 }}
              >
                ROI
              </Box>{" "}
              calculator?
            </Typography>

            <Typography
              sx={{
                fontSize: 16,
                lineHeight: 1.9,
                color: "var(--color-text-muted)",
                maxWidth: 420,
              }}
            >
              At Vision Nexus, we empower investors across Dubai and Sharjah to
              evaluate property opportunities with clarity. This ROI calculator
              goes beyond surface-level numbers, helping you understand true
              returns, financing impact, and long-term value before making
              critical investment decisions.
            </Typography>
          </Box>

          {/* RIGHT SIDE */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexWrap: "wrap",
              gap: { xs: 5, md: 8 },
            }}
          >
            {points.map((item) => (
              <Box
                key={item.no}
                sx={{
                  flex: {
                    xs: "1 1 100%",
                    md: "1 1 calc(50% - 32px)",
                  },
                }}
              >
                {/* NUMBER + LINE */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 20,
                      fontWeight: 600,
                      color: "var(--color-gold)",

                      minWidth: 28,
                    }}
                  >
                    {item.no}
                  </Typography>

                  <Box
                    sx={{
                      height: "1px",
                      flex: 1,
                      backgroundColor: "var(--color-gold)",
                    }}
                  />
                </Box>

                {/* TEXT */}
                <Typography
                  sx={{
                    fontSize: 18,
                    lineHeight: 1.7,
                    color: "var(--color-text-primary)",
                    fontWeight: 400,
                    maxWidth: 420,
                  }}
                >
                  {item.text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: "5%",
          right: "5%",
          height: "1px",
          background:
            "linear-gradient(90deg, transparent, var(--color-gold), transparent)",
          opacity: 0.6,
        }}
      />
    </Box>
  );
}

function FAQ() {
  const [openIndex, setOpenIndex] = React.useState(0);

  const faqs = [
    {
      q: "What is a good ROI for property investment in Sharjah?",
      a: "In Sharjah, investors typically target a net rental ROI between 6% and 10%. Actual returns depend on location, property type, tenant demand, and financing structure.",
    },
    {
      q: "Does this ROI calculator include capital appreciation?",
      a: "No. This calculator focuses purely on rental income performance. Capital appreciation varies by market cycle and should be assessed separately.",
    },
    {
      q: "Can I use this ROI calculator for Dubai real estate?",
      a: "Yes. The calculator works for Dubai properties as well, as long as realistic rental income, expenses, and financing inputs are provided.",
    },
    {
      q: "Is this calculator suitable for off-plan properties?",
      a: "Yes. You can estimate ROI using projected post-handover rental income and expected operating expenses.",
    },
    {
      q: "How accurate are the ROI results?",
      a: "The results are indicative and based on your inputs. For investment-grade decisions, Vision Nexus recommends validating figures with market data and professional advice.",
    },
  ];

  return (
    <Box
      sx={{
        backgroundColor: "var(--color-bg)",
        py: { xs: 10, md: 14 },
        position: "relative",
      }}
    >
      <Container maxWidth="md">
        {/* HEADING */}
        <Typography
          sx={{
            fontSize: { xs: 28, sm: 34, md: 42 },
            fontWeight: 500,
            color: "var(--color-text-primary)",
            textAlign: "center",
            mb: 2,
          }}
        >
          Frequently Asked Questions
        </Typography>

        <Typography
          sx={{
            fontSize: 16,
            color: "var(--color-text-muted)",
            textAlign: "center",
            mb: 8,
            lineHeight: 1.8,
          }}
        >
          Answers to common questions about ROI, rental yields, and property
          investment across Sharjah and the UAE.
        </Typography>

        {/* FAQ LIST */}
        <Box>
          {faqs.map((item, index) => {
            const isOpen = openIndex === index;

            return (
              <Box
                key={index}
                sx={{
                  mb: 3,
                  borderRadius: "16px",
                  backgroundColor: "var(--color-white)",
                  border: "1px solid rgba(0,0,0,0.06)",
                  boxShadow: isOpen
                    ? "0 20px 40px rgba(0,0,0,0.10)"
                    : "0 10px 25px rgba(0,0,0,0.06)",
                  transition: "all 0.3s ease",
                }}
              >
                {/* QUESTION */}
                <Box
                  onClick={() => setOpenIndex(isOpen ? -1 : index)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    px: { xs: 3, md: 4 },
                    py: 2.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 16.5,
                      fontWeight: 600,
                      color: isOpen
                        ? "var(--color-gold)"
                        : "var(--color-text-primary)",
                      pr: 2,
                    }}
                  >
                    {item.q}
                  </Typography>

                  <IconButton
                    size="small"
                    sx={{
                      color: isOpen
                        ? "var(--color-gold)"
                        : "var(--color-text-muted)",
                    }}
                  >
                    {isOpen ? <RemoveIcon /> : <AddIcon />}
                  </IconButton>
                </Box>

                {/* ANSWER */}
                <Collapse in={isOpen}>
                  <Box
                    sx={{
                      px: { xs: 3, md: 4 },
                      pb: 3,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 15.5,
                        lineHeight: 1.8,
                        color: "var(--color-text-muted)",
                      }}
                    >
                      {item.a}
                    </Typography>
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      </Container>
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: "5%",
          right: "5%",
          height: "1px",
          background:
            "linear-gradient(90deg, transparent, var(--color-gold), transparent)",
          opacity: 0.6,
        }}
      />
    </Box>
  );
}

function Disclaimer() {
  return (
    <Box
      sx={{
        backgroundColor: "rgba(0,0,0,0.03)",
        borderTop: "1px solid rgba(0,0,0,0.06)",
        py: { xs: 3, md: 4 },
      }}
    >
      <Container maxWidth="lg">
        <Typography
          sx={{
            fontSize: 13,
            lineHeight: 1.7,
            textAlign: "center",
            color: "var(--color-text-muted)",
            maxWidth: 920,
            mx: "auto",
          }}
        >
          <Box
            component="span"
            sx={{
              fontWeight: 600,
              color: "var(--color-text-primary)",
            }}
          >
            Disclaimer:
          </Box>{" "}
          The information provided by this ROI calculator is for illustrative
          purposes only and does not constitute financial, investment, or tax
          advice. Past performance is not indicative of future results. Before
          making any investment decision, we recommend consulting with an
          independent professional advisor.
        </Typography>
      </Container>
    </Box>
  );
}
